// AHA-47349-presenter-can-create-new-slide-type
// Developer slide types for the audience: look up the CREATOR's manifest base
// link (per environment) from the Manifest URL API, fetch that base +
// /manifest.json, and return its slide-type declarations normalized to the same
// shape the audience marketplace catalogue uses — with audienceUrl absolutized
// against the manifest link so SlidePluginContent can render the iframe.
// Relative import (not the `@/` alias): newly-added files are strict-linted with
// `import/no-unresolved`, and this repo's ESLint has no alias resolver configured.
import Config from '../config'

// When the Manifest URL API has no record for this user+env (or the call fails),
// fall back to the local dev server.
const DEFAULT_LINK = 'http://localhost:5173'

// The Manifest URL API is keyed by its own 5-env model (sandbox/local/staging/
// canary/production — what the presenter's DeveloperDomains UI saves under).
// Config.env (VUE_APP_ENV) CANNOT distinguish them — it is `develop` on both
// sandbox and staging, and `production` on both canary and production — so, like
// resolveMarketplaceApiUrl, derive the API env from the URL the build was
// compiled with (audience.dev.*/sandbox/ / audience.dev.* / audience2.* / ...).
// Keep in sync with the presenter app's copy so a creator's saved record is read by both.
const LOCAL_HOSTS = ['localhost', '127.0.0.1']
export const resolveManifestEnvFromUrl = (domainUrl) => {
  let host = ''
  let path = ''
  try {
    const url = new URL(domainUrl)
    host = url.hostname.toLowerCase()
    path = url.pathname
  } catch (e) {
    host = ''
  }
  if (LOCAL_HOSTS.includes(host)) return 'local'
  if (host.includes('.dev.') && path.startsWith('/sandbox/')) return 'sandbox'
  if (host.includes('.dev.')) return 'staging'
  // Canary uses the "<app>2.ahaslides.<tld>" host variant (presenter2/audience2).
  if (/2\.ahaslides\.(com|eu)$/.test(host)) return 'canary'
  if (host) return 'production'
  // No parseable host (misconfig): fall back to Config.env, else local.
  return Config.env === 'production' ? 'production' : 'local'
}
export const resolveManifestEnv = () => resolveManifestEnvFromUrl(Config.audienceUrl)

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '')

// Bounded fetch so an unreachable/slow manifest host can never stall the loader
// indefinitely. Abort each request after a hard timeout; on abort fetch rejects
// and the callers below fall back to DEFAULT_LINK / [].
const FETCH_TIMEOUT_MS = 5000
const fetchWithTimeout = async (url, options) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null
  try {
    return await fetch(url, controller ? { ...options, signal: controller.signal } : options)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// Make a (possibly relative) URL absolute against the manifest base link.
const absolutize = (base, url) => {
  const value = String(url || '')
  if (!value) return value
  if (/^https?:\/\//i.test(value)) return value
  return `${trimTrailingSlash(base)}${value.startsWith('/') ? '' : '/'}${value}`
}

// The manifest.json entries carry RELATIVE URLs (canvasUrl/audienceUrl/editorUrl/
// handlerUrl, icon, preview*). Turn every URL-ish key into a FULL link against the
// manifest base so the audience iframe/icons load from the developer's own domain.
// A key is URL-ish if its name ends with "url" (canvasUrl, previewUrl, …) or is
// exactly "icon". absolutize() leaves empty and already-absolute values untouched.
const isUrlKey = (key) => /url$/i.test(key) || key === 'icon'

const absolutizeUrlKeys = (cfg, base) => {
  const out = { ...cfg }
  Object.keys(out).forEach((key) => {
    if (isUrlKey(key) && typeof out[key] === 'string') {
      out[key] = absolutize(base, out[key])
    }
  })
  return out
}

// Resolve the manifest base link for a user + env, or null when there is no
// record for this env / the call fails. NULL — not DEFAULT_LINK — on a miss: a
// localhost fallback here would fire `http://localhost:5173/manifest.json` from a
// deployed audience build (Chrome exempts localhost from mixed-content blocking),
// letting any local app inject slide types into a production audience. localhost
// is applied by the caller for LOCAL builds only, never as a runtime fallback.
const resolveManifestLink = async (userId, env) => {
  try {
    const url = `${Config.manifestApiBaseUrl}/api/manifest`
      + `?userId=${encodeURIComponent(userId)}&env=${encodeURIComponent(env)}`
    const res = await fetchWithTimeout(url, { headers: { accept: 'application/json' } })
    if (!res.ok) return null
    const data = await res.json()
    const records = (data && data.records) || []
    // Only accept a record for THIS env — no cross-env fallback.
    const record = records.find((item) => item && item.env === env)
    return (record && record.url) || null
  } catch (e) {
    return null
  }
}

// Flatten a manifest declaration the way the audience marketplace normalizer
// does ({ ...topLevel, ...ahaConfig }), with every URL key absolutized against
// the manifest base link so they are full links, not paths.
const normalize = (entry, link) => {
  if (!entry) return null
  const cfg = entry.ahaConfig && typeof entry.ahaConfig === 'object' ? entry.ahaConfig : {}
  const { ahaConfig, ...topLevel } = entry
  // Merge (ahaConfig wins — audience convention), THEN absolutize the MERGED
  // result so every URL key is a full link whether it came from ahaConfig, a flat
  // entry (no ahaConfig), or a top-level override.
  return absolutizeUrlKeys({
    ...topLevel,
    ...cfg,
    plugin: entry.plugin != null ? entry.plugin : cfg.plugin,
  }, link)
}

/**
 * Returns the creator's developer slide types for the current env, or [] when
 * there is nothing to add (no manifest.json, or a failed request).
 */
export const fetchDeveloperSlideTypes = async ({ userId, env = resolveManifestEnv() }) => {
  if (!userId || !env) return []

  let link = await resolveManifestLink(userId, env)
  // No record for this env: on a LOCAL build fall back to the local dev server;
  // on any deployed build a miss yields nothing (never fetch localhost from a
  // deployed audience).
  if (!link) {
    if (env !== 'local') return []
    link = DEFAULT_LINK
  }

  // Fetch <link>/manifest.json. If it doesn't exist, do nothing further.
  let manifest
  try {
    const res = await fetchWithTimeout(`${trimTrailingSlash(link)}/manifest.json`, {
      headers: { accept: 'application/json' },
    })
    if (!res.ok) return []
    manifest = await res.json()
  } catch (e) {
    return []
  }

  const entries = Array.isArray(manifest) ? manifest : (manifest && manifest.slideTypes) || []
  return entries
    .map((entry) => normalize(entry, link))
    .filter((t) => t && typeof t.type === 'string')
}

export default { fetchDeveloperSlideTypes }
